import Log from "@/debug/Log";
import { ObjectId } from "@/model/db/General";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import { openOrDownloadDocument } from "@/redux/actions/ui-config/ui-config-actions";
import { store } from "@/redux/store";
import ServiceUtils from "@/services/ServiceUtils";
import { hasValue } from "@/utils/Helpers";
import moment from "moment";
import { nanoid } from "nanoid";
import i18n from "../../../i18n";
import { AssetTypes } from "../../../model/AssetTypes";
import {
  ApproveInvoiceTaskAsset,
  CompleteInvoiceFollowUpTaskAsset,
} from "../../../model/general-assets/TaskAsset";
import CDNService from "../../../services/CDNService";
import CacheService from "../../../services/CacheService";
import DataBus from "../../../services/DataBus";
import DataBusDefaults from "../../../services/DataBusDefaults";
import { DataBusSubKeys } from "../../../utils/Constants";
import FileUtils from "../../../utils/FileUtils";
import { HTTP } from "../../../utils/Http";
import StringUtils from "../../../utils/StringUtils";
import {
  DeclineReasonActionType,
  TypeOfBankTransfer,
} from "../invoiceConfigApp/RACInterfaces";
import {
  BookInvoiceFormData,
  Invoice,
  InvoiceBookingEntryForm,
  InvoiceDirection,
  InvoiceExportHistory,
  InvoiceKind,
  ManualBookingData,
  RAInvoiceCostCenter,
} from "./RAInterfaces";
import { RA_INVOICE_TABLE_IDENTIFIER } from "./RAUtils";
import {
  InvoiceBaseFormModel,
  InvoiceDefaultFormModel,
  InvoiceDunningFormModel,
  InvoiceFullForm,
  InvoiceIncomingFormModel,
  InvoiceOutgoingFormModel,
  InvoiceStornoFormModel,
} from "./components/InvoiceForm/RAInvoiceFormModel";

export type InvoiceCreationOptions = {
  linkedInvoice?: string;
  linkedActivity?: {
    assetId: string;
    assetType: string;
  };

  isStorno?: boolean;
};
export type DeclineInvoiceDecline = {
  declineType: "decline";
  comment: string;
};
export type DeclineInvoiceData = {
  comment: string;
  reasonId: ObjectId;
  assigneeUsers: ObjectId[];
  force?: boolean;
  todo: {
    id: ObjectId | null;
    value: string;
  }[];
};

class InvoiceServiceClass {
  getInitialDataForInvoice = (
    invoice: Invoice,
    opt?: {
      actionType?: DeclineReasonActionType;
      referenceInvoice: Invoice;
      linkedActivity?: {
        assetId: string;
        assetType: string;
      };
    }
  ) => {
    const extractField = (
      field: string,
      defaultValue: any,
      formatValue?: (value: any) => any
    ) => {
      const extractedValue = invoice?.extract?.[field];
      if (hasValue(extractedValue)) {
        return formatValue ? formatValue(extractedValue) : extractedValue;
      }
      return defaultValue;
    };

    // const allCustomFields = ACInvoiceUtils.getCustomFields(invoice?.data?.type);
    const base: InvoiceFullForm = {
      direction: invoice?.data?.direction,
      activity: invoice.data.activity?.map((e) => e) || [],
      // custom:
      //   allCustomFields.map((e) => ({
      //     identifier: e.id,
      //     value:
      //       invoice.data.invoice?.custom?.find((a) => a.identifier == e.id)
      //         ?.value || "",
      //   })) || [],
      contact: invoice.data.invoice.contact,
      invoiceId:
        invoice.data.invoice.invoiceId ||
        extractField("invoiceNumber", invoice.data.invoice.invoiceId),
      documentDate:
        invoice.data.invoice.documentDate ||
        extractField("invoiceDate", invoice.data.invoice.documentDate),
      entity: invoice.data.entity,
      invoiceType: invoice.data.invoice.invoiceType,
      objectId: invoice.data.objectId,
      // paymentPlan: [], // TODO_INVOICE (invoice as InvoiceIncomingData).data.payment.paymentPlan || [],
      tags: invoice.data.tags || [],
      // value: {
      //   amount: invoice.data.invoice.value?.amount || 0,
      //   currency:
      //     invoice.data.invoice.value?.currency || StringUtils.getCurrency(),
      // },
      approvalChainChange: false,
      urgent: invoice.data.urgent?.isUrgent || false,
      urgentComment: invoice.data.urgent?.urgentComment || "",
      costCenter: invoice.data.costCenter || [],
      currency:
        invoice.data.invoice.value?.currency ||
        extractField("currencyCode", StringUtils.getCurrency()),
      paymentPlan:
        invoice.data.payment?.length > 0
          ? invoice.data.payment
          : [
              {
                amount: extractField("paymentAmountTotal", 0),
                date: new Date(),
                usage: extractField(
                  "invoiceNumber",
                  invoice.data.invoice.invoiceId
                ),
                createPaymentObject: true,
              },
            ],
      installment: invoice.data.installment || [],
      invoiceKind: invoice.data.kind || InvoiceKind.INVOICE,
      paymentType: invoice.data.invoice?.paymentType || null,
      paymentTypeData: invoice.data.invoice?.paymentTypeData || null,
      // accountNumber: null,

      // addressOfBank: null,
      // bic: null,
      // iban: null,
      // nameOfBank: null,
      // routing: null,
      // swift: null,
      // typeOfBankTransfer: null,
    };
    if (invoice.data.kind === InvoiceKind.DUNNING) {
      base.linkedInvoice = invoice.data.reminderFor;
      base.dunningLevel = invoice.data.reminderLevel;
      base.dunningValue = invoice.data.invoice.value.amount;
    }
    if (invoice.data.kind === InvoiceKind.STORNO) {
      base.linkedInvoice = invoice.data.correction?.linkedInvoice;

      base.stornoValue = invoice.data.invoice.value.amount;
    }

    if (opt?.actionType) {
      if (opt.actionType === DeclineReasonActionType.NEED_CORRECTION) {
        base.invoiceKind = InvoiceKind.INVOICE;
      }
      if (
        opt.actionType === DeclineReasonActionType.NEED_STORNO &&
        opt.referenceInvoice
      ) {
        base.invoiceKind = InvoiceKind.STORNO;
        base.linkedInvoice = opt.referenceInvoice._id;
        base.invoiceType = opt.referenceInvoice.data.invoice.invoiceType;
      }
    }
    if (opt?.linkedActivity) {
      base.activity = [opt.linkedActivity];
    }
    if (opt?.referenceInvoice) {
      base.objectId = opt.referenceInvoice.data.objectId;
      base.contact = opt.referenceInvoice.data.invoice.contact;
      base.entity = opt.referenceInvoice.data.entity;
    }

    if (invoice?.data?.direction === "INCOMING") {
      const formData: Partial<InvoiceDefaultFormModel> = {
        ...base,
        direction: invoice?.data?.direction,
        costCenter: invoice.data.costCenter || [],
        paymentTypeData: invoice.data.invoice.paymentTypeData,
        // ? (Object.fromEntries(
        //     Object.entries(invoice.data.invoice.paymentTypeData).map(
        //       ([key, value]) => [key, value === null ? "" : value]
        //     )
        //   ) as PaymentData)
        // : {
        //     type: "NONE",
        //   },
      };
      return formData;
    } else if (invoice?.data?.direction === "OUTGOING") {
      // TODO
      const formData: InvoiceOutgoingFormModel = {
        ...base,
        direction: invoice?.data?.direction,
        // dueDate: invoice.data.invoice.dueDate,
      };
      return formData;
    } else {
      throw new Error("no valid payment direction");
    }
  };

  async setCostCentersForInvoice(
    invoice: Invoice,
    costCenters: RAInvoiceCostCenter[]
  ) {
    if (invoice?.data?.direction === "INCOMING") {
      const invoiceFormData: InvoiceDefaultFormModel =
        this.getInitialDataForInvoice(invoice) as InvoiceDefaultFormModel;
      invoiceFormData.costCenter = costCenters;

      return await this.updateInvoice(invoice._id, invoiceFormData);
    } else {
      throw new Error("invalid payment direction");
    }
  }

  async downloadBookingInvoices(
    invoices: string[],
    splittedByObject: boolean,
    filenameWithoutExtension: string
  ) {
    FileUtils.forceDownloadFile(
      `/api/invoice/downloadInvoices?param=${encodeURIComponent(
        JSON.stringify({
          invoiceIds: invoices,
          splitObject: splittedByObject,
        })
      )}`,
      `${filenameWithoutExtension}.zip`
    );
  }

  async deprecated_setCheckedOfInvoice(invoiceId: string, checked: boolean) {
    const result = await HTTP.post({
      url: `/api/invoice${invoiceId}/setCheckedStatus`,
      target: "EMPTY",
    });

    return result;
  }

  async queryInvoicesToCheck(types: string[]) {
    const data = await HTTP.get({
      url: `/api/invoice/invoicesToCheck`,
      target: "EMPTY",
      queryParams: {
        param: {
          types,
          direction: "INCOMING",
        },
      },
    });

    return data;
  }

  async changeInvoiceAssignment(
    invoice: Invoice,
    invoiceData: { invoiceType: string; objectId: string; entity: string }
  ) {
    const defaultData: InvoiceFullForm = this.getInitialDataForInvoice(invoice);

    defaultData.invoiceType = invoiceData.invoiceType;
    defaultData.objectId = invoiceData.objectId;
    defaultData.entity = invoiceData.entity;

    defaultData.approvalChainChange = true;

    const data = await this.updateInvoice(invoice._id, defaultData);

    return data;
  }

  async setPaymentDueDate(invoiceIds: string[], date: Date) {
    const result = await HTTP.post({
      url: `/api/invoice/setPaymentDueDate`,
      target: "EMPTY",
      bodyParams: { dueDate: date.toISOString(), invoiceIds },
    });

    return result;
  }

  async markedInvoicesBooked(ids: string[]) {
    const result = await HTTP.post({
      url: `/api/invoice/approveBookings`,
      target: "EMPTY",
      bodyParams: {
        invoiceIds: ids,
      },
    });

    return result;
  }

  async declineInvoice(
    invoiceId: string,
    data: DeclineInvoiceData,
    force?: boolean
  ) {
    if (force) {
      data.force = true;
    }
    let result;
    result = await HTTP.post({
      url: `/api/invoice/${invoiceId}/declineInvoice`,
      target: "EMPTY",
      bodyParams: data,
    });
    await CacheService.getData({
      id: invoiceId,
      oType: "asset",
      assetType: AssetTypes.Invoicing.Invoice,
      forceReload: true,
      silentReload: true,
    });
    return result;
  }

  async queryBookingInvoices(direction: InvoiceDirection, types: string[]) {
    const data = await HTTP.get({
      url: `/api/invoice/invoicesToBook`,
      target: "EMPTY",
      queryParams: {
        param: {
          direction,
          types,
        },
      },
    });

    return data;
  }

  async finishCompleteTask(
    invoice: Invoice,
    task: CompleteInvoiceFollowUpTaskAsset,
    comment: string
  ) {
    const result = await HTTP.post({
      url: `/api/invoice${invoice._id}/completeFollowUp`,
      target: "EMPTY",
      bodyParams: {
        comment: comment || undefined,
      },
    });

    return result;
  }
  async approveInvoiceTask(
    invoice: Invoice,
    task: ApproveInvoiceTaskAsset,
    comment: string
  ) {
    const result = await HTTP.post({
      url: `/api/invoice/${invoice._id}/acceptApproval`,
      target: "EMPTY",
      bodyParams: {
        comment: comment || undefined,
      },
    });

    CacheService.getData({
      oType: "asset",
      id: invoice._id,
      assetType: AssetTypes.Invoicing.Invoice,
      forceReload: true,
      silentReload: true,
    });

    return result;
  }

  generateFilenameForInvoice(invoice: Invoice) {
    const invoiceId = invoice.data.invoice?.invoiceId || "";
    const timestamp = moment(invoice.meta.ca).format("YYYY-MM-DD");
    return `${timestamp}_${invoice.id}_${invoiceId}.pdf`;
  }

  async uploadNewInvoices(
    direction: InvoiceDirection,
    type: string,
    files: File[],
    onProgress?: (progress: number, index: number, file: File) => void,
    reloadTableIdentifiers?: string[],
    options?: InvoiceCreationOptions
  ) {
    let index = 0;
    for (const file of files) {
      await this.uploadNewInvoice(
        direction,
        type,
        file,
        (progress) => {
          if (onProgress) onProgress(progress, index, file);
        },
        options
      );
      index++;
    }
    DataBus.emit(DataBusSubKeys.RELOAD, {
      identifiers: [
        RA_INVOICE_TABLE_IDENTIFIER,
        ...(reloadTableIdentifiers || []),
      ],
    });
  }
  async uploadNewInvoice(
    direction: InvoiceDirection,
    type: string,
    file: File,
    onProgress?: (progress: number) => void,
    options?: InvoiceCreationOptions
  ) {
    const tmpId = nanoid();
    const fileUpload = (await CDNService.uploadFile({
      assetField: "data.invoice.file",
      assetType: "invoice",
      file,
      filename: file.name,
      tempID: tmpId,
      onProgress,
    })) as { cdnID: string };

    const cdnID = fileUpload.cdnID;
    const invoice = await this.createNewInvoice(
      direction,
      cdnID,
      type,
      options
    );
    return invoice;
  }

  private async createNewInvoice(
    direction: InvoiceDirection,
    cdnId: string,
    type: string,
    options?: InvoiceCreationOptions
  ) {
    return await HTTP.post({
      url: `/api/invoice/createInvoice`,
      target: "EMPTY",
      bodyParams: {
        cdnId,
        direction,
        type,

        // ... (options || []),
      },
    });

    // return new Promise((resolve, reject) => {

    //   SubmitService.submitData({
    //     type: "asset",
    //     assetType: "invoice",
    //     data: {
    //       data: {
    //         direction,
    //         type: type,
    //         cdnId: cdnId,
    //         ...(options || []),
    //       },
    //     },
    //     ignorePropChecks: true,
    //     ignoreSubmitValidation: true,

    //     onError: (err) => reject(err),
    //     onSuccess: (data) => resolve(data),
    //   });
    // });
  }
  async updateInvoice(invoiceId: string, invoiceData: InvoiceFullForm) {
    try {
      const data = this.convertBody(invoiceData, false, true);

      const invoice = (await HTTP.post({
        url: `/api/invoice/${invoiceId}/editInvoice`,
        target: "EMPTY",
        bodyParams: {
          invoiceKind: invoiceData.invoiceKind,
          data: data,
          approvalChainChange: invoiceData.approvalChainChange,
          // forceUpload: force,
          // ...otherInvoiceData,
          // custom: invoiceData.custom.filter((e) => e.value),
          // objectId: invoiceData.objectId || undefined,
        },
      })) as Invoice;
      CacheService.updateDataInCaches(invoice._id, invoice, "patchRoot");

      if (invoice.intern?.invoiceTaskId) {
        await CacheService.load(
          AssetTypes.Task,
          invoice.intern.invoiceTaskId,
          true,
          true
        );
      }
      return invoice as Invoice;
    } catch (err) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "ra:InvoiceService.updateFailed",
          "Die Rechnung konnte nicht gespeichert werden"
        ),
      });
      throw err;
    }
  }
  async requestInvoiceChange(invoiceId: string, invoiceData: InvoiceFullForm) {
    try {
      const data = this.convertBody(invoiceData, false, true);
      const invoice = await HTTP.post({
        url: `/api/invoice/${invoiceId}/changeRequest`,
        target: "EMPTY",
        bodyParams: {
          invoiceKind: invoiceData.invoiceKind,
          data: data,
          comment: invoiceData.comment,
          // forceUpload: force,
          // ...otherInvoiceData,
          // custom: invoiceData.custom.filter((e) => e.value),
          // objectId: invoiceData.objectId || undefined,
        },
      });
      CacheService.updateDataInCaches(invoice._id, invoice, "patchRoot");
      DataBusDefaults.toast({
        type: "success",
        text: i18n.t(
          "ra:InvoiceService.changeRequestSent",
          "Die Änderungsanfrage wurde erfolgreich gesendet"
        ),
        duration: 6000,
      });
      return invoice as Invoice;
    } catch (err) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "ra:InvoiceService.updateFailed",
          "Die Rechnung konnte nicht gespeichert werden"
        ),
      });
      throw err;
    }
  }

  async deleteInvoice(invoice: Invoice) {
    await HTTP.delete({
      url: `/api/invoice/${invoice._id}`,
      target: "EMPTY",
    });

    CacheService.updateDataInCaches(
      invoice._id,
      { _deleted: true },
      "patchRoot"
    );
  }
  private convertBody(
    invoiceData: InvoiceBaseFormModel,
    force: boolean,
    isUpdate = false
  ) {
    const { approvalChainChange, ...otherInvoiceData } = invoiceData;

    let body = {
      forceUpload: force,
      invoiceKind: invoiceData.invoiceKind,
      entity: invoiceData.entity,
      objectId: invoiceData.objectId || null,
      currency: invoiceData.currency,
      invoiceType: invoiceData.invoiceType,
      contact: invoiceData.contact,
      documentDate: invoiceData.documentDate,
      invoiceId: invoiceData.invoiceId,
      // custom: [],
      activity: invoiceData.activity,
      tags: invoiceData.tags,
    } as any;

    if (invoiceData.invoiceKind === InvoiceKind.INVOICE) {
      const invoiceDataCasted = invoiceData as InvoiceDefaultFormModel;

      const modeOfPayment = InvoiceStruct.getModeOfPayment(
        invoiceData.direction,
        invoiceDataCasted.paymentType
      );

      body = {
        ...body,

        costCenter:
          invoiceDataCasted.costCenter?.map((e) => ({
            ...e,
            objectId: e.objectId || null,
          })) || [],
        paymentPlan: invoiceDataCasted.paymentPlan?.map((e) => ({
          amount: e.amount,
          date: e.date,
          usage: e.usage,
          createPaymentObject:
            modeOfPayment?.kind === "bankTransfer"
              ? e.createPaymentObject
              : false,
          discounts: e.discounts,
        })),
        paymentType: invoiceDataCasted.paymentType,
        installment:
          invoiceDataCasted.installment?.map((e) => ({
            amount: e.amount,
            date: e.date,
            usage: e.usage,
            createPaymentObject:
              modeOfPayment?.kind === "bankTransfer"
                ? e.createPaymentObject
                : false,
          })) || [],
        urgent: invoiceDataCasted.urgent,
        urgentComment: invoiceDataCasted.urgentComment,
      };

      if (invoiceData.direction === "INCOMING") {
        const invoiceDataIncoming = invoiceData as InvoiceIncomingFormModel;
        body.paymentTypeData = {
          type: invoiceDataIncoming.paymentTypeData.type || "NONE",
          iban:
            invoiceDataIncoming.paymentTypeData.type ===
              TypeOfBankTransfer.PAYMENT_IBAN ||
            invoiceDataIncoming.paymentTypeData.type ===
              TypeOfBankTransfer.PAYMENT_IBAN_INTERNATIONAL
              ? invoiceDataIncoming.paymentTypeData.iban
              : undefined,

          bic:
            invoiceDataIncoming.paymentTypeData.type ===
            TypeOfBankTransfer.PAYMENT_IBAN_INTERNATIONAL
              ? invoiceDataIncoming.paymentTypeData.bic
              : undefined,

          // nameOfBank:
          //   invoiceDataIncoming.paymentTypeData.type ===
          //   TypeOfBankTransfer.PAYMENT_INTERNATIONAL
          //     ? invoiceDataIncoming.paymentTypeData.nameOfBank
          //     : undefined,
          // addressOfBank:
          //   invoiceDataIncoming.paymentTypeData.type ===
          //   TypeOfBankTransfer.PAYMENT_INTERNATIONAL
          //     ? invoiceDataIncoming.paymentTypeData.addressOfBank
          //     : undefined,
          swift:
            invoiceDataIncoming.paymentTypeData.type ===
            TypeOfBankTransfer.PAYMENT_INTERNATIONAL
              ? invoiceDataIncoming.paymentTypeData.swift
              : undefined,
          routing:
            invoiceDataIncoming.paymentTypeData.type ===
            TypeOfBankTransfer.PAYMENT_INTERNATIONAL
              ? invoiceDataIncoming.paymentTypeData.routing
              : undefined,
          account:
            invoiceDataIncoming.paymentTypeData.type ===
            TypeOfBankTransfer.PAYMENT_INTERNATIONAL
              ? invoiceDataIncoming.paymentTypeData.account
              : undefined,
        };
      }
    }

    if (invoiceData.invoiceKind === InvoiceKind.DUNNING) {
      const invoiceDataCasted = invoiceData as InvoiceDunningFormModel;
      body = {
        ...body,
        value: {
          amount: invoiceDataCasted.dunningValue,
          currency: invoiceDataCasted.currency,
        },
        reminderLevel: invoiceDataCasted.dunningLevel,
        reminderFor: isUpdate ? undefined : invoiceDataCasted.linkedInvoice,
      };
    }

    if (invoiceData.invoiceKind === InvoiceKind.STORNO) {
      const invoiceDataCasted = invoiceData as InvoiceStornoFormModel;
      body = {
        ...body,
        value: {
          amount: invoiceDataCasted.stornoValue,
          currency: invoiceDataCasted.currency,
        },
        // value: {
        //   amount: invoiceDataCasted.dunningValue,
        //   currency: invoiceDataCasted.currency,
        // },
        stornoFor: isUpdate ? undefined : invoiceDataCasted.linkedInvoice,
      };
    }

    if (invoiceData.direction === "OUTGOING") {
    }

    return body;
  }
  async startApprovalForInvoice(
    invoiceId: string,
    invoiceData: InvoiceBaseFormModel,
    force: boolean
  ) {
    let body = this.convertBody(invoiceData, force);

    const approval = await HTTP.post({
      url: `/api/invoice/${invoiceId}/startInvoiceProcess`,
      target: "EMPTY",
      bodyParams: {
        invoiceKind: invoiceData.invoiceKind,
        data: { ...body },
        // forceUpload: force,
        // ...otherInvoiceData,
        // custom: invoiceData.custom.filter((e) => e.value),
        // objectId: invoiceData.objectId || undefined,
      },
    });

    return approval as Invoice;
  }
  async abortExtraction(invoice: Invoice) {
    const approval = await HTTP.post({
      url: `/api/invoice/${invoice._id}/abortExtraction`,
      target: "EMPTY",
    });
    return approval;
  }

  async addActivity(
    invoice: Invoice,
    activity: { assetId: string; assetType: string }
  ) {
    const formData = this.getInitialDataForInvoice(invoice);
    formData.activity = [...(formData.activity || []), activity];
    return this.updateInvoice(invoice._id, formData);
  }
  async removeActivity(
    invoice: Invoice,
    activity: { assetId: string; assetType: string }
  ) {
    const formData = this.getInitialDataForInvoice(invoice);
    formData.activity = (formData.activity || []).filter(
      (e) => e.assetId !== activity.assetId
    );
    return this.updateInvoice(invoice._id, formData);
  }

  convertBookingForm(data: BookInvoiceFormData, writeContactAccount: boolean) {
    const dataToPush: {
      contactAccount?: string;
      paymentPlan: InvoiceBookingEntryForm[];
      installment?: InvoiceBookingEntryForm[];
    } = {
      contactAccount: writeContactAccount ? data.contactAccount : undefined,
      paymentPlan: data.paymentPlan.map(
        ({ BUCode, accountNumberCode, amount, currency, ...other }) => ({
          ...other,
          cost1: other.cost1 || "",
          cost2: other.cost2 || "",
        })
      ),
      installment:
        data.installment
          ?.map((a) => a.entries)
          .flat()
          .map(({ BUCode, accountNumberCode, amount, currency, ...other }) => ({
            ...other,
            cost1: other.cost1 || "",
            cost2: other.cost2 || "",
          })) || [],
    };

    return dataToPush;
  }

  async bookInvoice(
    invoiceId: string,
    data: BookInvoiceFormData,
    writeContactAccount: boolean
  ) {
    return ServiceUtils.toastError(async () => {
      const dataToPush = this.convertBookingForm(data, writeContactAccount);
      await HTTP.post({
        url: `/api/invoice/${invoiceId}/bookInvoice`,
        target: "EMPTY",
        bodyParams: dataToPush,
      });

      const result = CacheService.load(
        AssetTypes.Invoicing.Invoice,
        invoiceId,
        true,
        true
      );

      return result;
    });
  }

  async keepBookingOfDeclinedInvoice(invoiceId: string) {
    return ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        url: `/api/invoice/${invoiceId}/keepBookingOfDeclinedInvoice`,
        target: "EMPTY",
      });
      result._booked = false;
      CacheService.update(result);

      return result;
    });
  }

  async revertBooking(invoiceId: string, goBackToBooking = true) {
    return ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        url: `/api/invoice/${invoiceId}/${goBackToBooking}/revertInvoice`,
        target: "EMPTY",
      });
      result._booked = false;
      CacheService.update(result);

      return result;
    });
  }

  async downloadInvoiceBookings(
    entityIds: string[],
    downloadType: "PDF" | "DATEV",
    direction: InvoiceDirection
  ) {
    return ServiceUtils.toastError(
      async () => {
        const filename = `${moment().format(
          "YYYY-MM-DD"
        )}_${direction}_${downloadType}.zip`;

        const result = await HTTP.post({
          url: `/api/invoice/downloadBooking`,
          target: "EMPTY",
          bodyParams: {
            entity: entityIds,
            downloadType,
            direction,
          },
        });

        Log.info("##InvoiceService - downloadInvoiceBookings", result);
        return result;
      },
      async (err) => {
        if (err?.response?.status === 300) {
          // retur nundefined so default toast will not be shown
          return undefined;
        }

        return null;
      }
    );
  }

  async downloadHistoryZip(entry: InvoiceExportHistory) {
    const url = await CDNService.fetchCDNLink({
      assetType: AssetTypes.Invoicing.ExportHistory,
      assetId: entry._id,
      assetField: "data.file",
      cdnId: "",
      hasFolderReadPermissions: true,
      fileKey: entry.data.file.key,
    });

    store.dispatch(
      openOrDownloadDocument(
        url,
        FileUtils.mimeToExt(entry.data.file.content_type),
        `${StringUtils.formatDate(entry.meta.ca, "YYYY-MM-DD")}.zip`
      )
    );
  }

  async linkInvoiceWithFollowUp(
    invoiceId: ObjectId,
    linkedInvoice: ObjectId,
    actionType: DeclineReasonActionType,
    activityId: ObjectId
  ) {
    const result = (await HTTP.post({
      url: `/api/invoice/${invoiceId}/linkInvoice`,
      target: "EMPTY",
      bodyParams: {
        linkedInvoice,
        type:
          actionType === DeclineReasonActionType.NEED_STORNO
            ? "STORNO"
            : "CORRECTION",
        activityId,
      },
    })) as Invoice;
    CacheService.update(result);
    return result;
  }

  async accepptInvoiceChangeRequest(
    requestId: ObjectId,
    formData: any,
    writeContactAccount: boolean
  ) {
    return ServiceUtils.toastError(async () => {
      const invoiceData = this.convertBody(formData, false, true);
      const bookingData =
        formData.ignoreBookingChange && !formData.forceBookingChange
          ? null
          : this.convertBookingForm(formData.accounting, writeContactAccount);
      // const invoice = await HTTP.post({
      //   url: `/api/invoice/${invoiceId}/editInvoice`,
      //   target: "EMPTY",
      //   bodyParams: {
      //     invoiceKind: invoiceData.kind,
      //     data: data,
      //     // forceUpload: force,
      //     // ...otherInvoiceData,
      //     // custom: invoiceData.custom.filter((e) => e.value),
      //     // objectId: invoiceData.objectId || undefined,
      //   },
      // });

      const result = await HTTP.post({
        url: `/api/invoice/${requestId}/acceptChangeRequest`,
        target: "EMPTY",
        bodyParams: {
          request: {
            invoiceKind: formData.invoiceKind,
            data: invoiceData,
            // forceUpload: force,
            // ...otherInvoiceData,
            // custom: invoiceData.custom.filter((e) => e.value),
            // objectId: invoiceData.objectId || undefined,
          },
          booking: bookingData,
        },
      });
      CacheService.update(result);

      return result;
    });
  }

  async declineInvoiceChangeRequest(requestId: ObjectId, comment: string) {
    return ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        url: `/api/invoice/${requestId}/declineChangeRequest`,
        target: "EMPTY",
        bodyParams: {
          comment,
        },
      });

      return result;
    });
  }

  async deferInitInvoice(invoiceId: string, comment: string) {
    return ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        url: `/api/invoice/${invoiceId}/deferInitInvoice`,
        target: "EMPTY",
        bodyParams: {
          comment,
        },
      });
      CacheService.update(result);

      return result;
    });
  }

  async addManualBooking(invoiceId: string, data: ManualBookingData) {
    return ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        url: `/api/invoice/${invoiceId}/manualBooking`,
        target: "EMPTY",
        bodyParams: {
          value: data.amount,
          comment: data.comment,
          booking: data.withBooking
            ? {
                BU: data.booking.BU,
                accountNumber: data.booking.accountNumber,
                referenceField: data.booking.referenceField || "",
                date: data.booking.date || null,
                serviceDate: data.booking.serviceDate || null,
                usage: data.booking.usage || "",
                cost1: data.booking.cost1 || "",
                cost2: data.booking.cost2 || "",
                extraInfo: data.booking.extraInfo || [],
                value: data.amount,
              }
            : null,
        },
      });
      CacheService.update(result);

      return result;
    });
  }
  async deleteManualBooking(invoiceId: string, manualLinkId: string) {
    return ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        url: `/api/invoice/${invoiceId}/${manualLinkId}/unlinkManualBooking`,
        target: "EMPTY",
      });
      CacheService.update(result);

      return result;
    });
  }
}
const InvoiceService = new InvoiceServiceClass();
export default InvoiceService;
